import React, { useState } from "react";
import { encryptPassword } from "../../utils/cryptoUtils";
import { useLocation, useNavigate } from "react-router-dom";
import ActiveSessionDialog from "./ActiveSessionDialog";
import { fetchLoginDetails } from "./../../API/UserApi";
import nogruntblacklogo from "../../Assets/nogruntblacklogo.svg";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useNotification } from "../../hooks";
import { HelpIconButton } from "../../components-new/help/help-modal";
import { API_BASE_PATH } from "../../Config/Config";
import { getBrandLogo } from "../../utils/userDetailsUtils";

const LoginPage = (props) => {
  const notify = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [account, setAccount] = useState({
    email: "",
    password: "",
  });
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  const logoToShow = getBrandLogo(nogruntblacklogo);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handelAccount = (property, event) => {
    let value = event.target.value;
    if (property === "email") {
      value = value.replace(/[<>"'`\\|(){}[\]]/g, "");
    }
    setAccount({ ...account, [property]: value });
  };

  const handelLogin = async (forceLogin = false) => {
    if (!account.email || !account.password) {
      notify("Please fill in all fields.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const encryptedPassword = await encryptPassword(account.password);
      await fetchLoginDetails({
        pwd: encryptedPassword,
        uname: account.email,
        ...(forceLogin && { forceLogin: true }),
      })
        .then((response) => {
          if (response.status === 200) {
            if (response.data.message === "success") {
              localStorage.setItem(
                "userDetail",
                JSON.stringify({
                  email: account.email,
                  ...response.data,
                  companyid: response.data?.company?.idcompany,
                  usertype: response.data?.usertype,
                  token: response.data?.randomkey,
                }),
              );
              localStorage.setItem(
                "colorcode",
                JSON.stringify({
                  primaryColor: response.data?.company?.primaryColor,
                  secondaryColor: response.data?.company?.secondaryColor,
                  tertiaryColor: response.data?.company?.tertiaryColor,
                  brandLogo: response.data?.company.brandLogo,
                  companyurl: response.data?.company.companyurl,
                }),
              );
              const redirectAfterLogin = localStorage.getItem("redirectAfterLogin");
              if (redirectAfterLogin) {
                localStorage.removeItem("redirectAfterLogin");
                window.location.replace(redirectAfterLogin);
              } else {
                // replace (not push) so the login page entry is gone from history -
                // the back button can no longer land a user back on it once authenticated
                navigate("/new", { replace: true });
              }
            } else if (response.data.message === "active_session") {
              setShowSessionDialog(true);
            } else if (
              response.data.message &&
              response.data.message.toLowerCase().includes("blocked")
            ) {
              notify(response.data.message, "error");
            } else {
              notify("Invalid Username or Password!, Please try Again.", "error");
            }
          }
        })
        .catch(() => {});
    } catch (error) {
      notify("Login failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoLogin = () => {
    window.location.href = `${API_BASE_PATH}/KeyLogging/sso/login`;
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 px-4 py-6">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 w-48">
            <img src={logoToShow} alt="Logo" className="h-14 mr-2" />
            <HelpIconButton path={path} />
          </div>

          {/* Title */}
          <div className="mb-8">
            <h1
              className="text-2xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: "Urbanist, sans-serif" }}
            >
              Welcome back
            </h1>
            <p className="text-gray-500" style={{ fontFamily: "Urbanist, sans-serif" }}>
              Sign in to continue automating your tests
            </p>
          </div>

          {/* SSO Button */}
          <button
            type="button"
            onClick={handleSsoLogin}
            className="w-full flex items-center justify-center gap-3 px-4 py-3.5 mb-6 border border-gray-200 rounded-xl bg-white text-gray-700 font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 cursor-pointer"
            style={{ fontFamily: "Urbanist, sans-serif" }}
          >
            <svg width="20" height="20" viewBox="0 0 23 23" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F65314" />
              <rect x="13" y="1" width="9" height="9" fill="#7CBB00" />
              <rect x="1" y="13" width="9" height="9" fill="#00A1F1" />
              <rect x="13" y="13" width="9" height="9" fill="#FFBB00" />
            </svg>
            Continue with Microsoft
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-gray-400" style={{ fontFamily: "Urbanist, sans-serif" }}>
              or
            </span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Login Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handelLogin();
            }}
            className="space-y-5"
          >
            {/* Email Field */}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                Email address
              </label>
              <input
                type="email"
                required
                autoComplete="off"
                value={account.email}
                onChange={(event) => handelAccount("email", event)}
                onFocus={() => setFocusedField("email")}
                onBlur={() => setFocusedField(null)}
                placeholder="Enter your email"
                className={`w-full px-4 py-3 border-2 rounded-xl outline-none transition-all duration-200 ${
                  focusedField === "email"
                    ? "border-blue-500 bg-white"
                    : "border-gray-200 bg-gray-50 hover:border-gray-300"
                }`}
                style={{ fontFamily: "Urbanist, sans-serif" }}
              />
            </div>

            {/* Password Field */}
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-2"
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="off"
                  value={account.password}
                  onChange={(event) => handelAccount("password", event)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Enter your password"
                  className={`w-full px-4 py-3 pr-12 border-2 rounded-xl outline-none transition-all duration-200 ${
                    focusedField === "password"
                      ? "border-blue-500 bg-white"
                      : "border-gray-200 bg-gray-50 hover:border-gray-300"
                  }`}
                  style={{ fontFamily: "Urbanist, sans-serif" }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate("/ForgotPasswordPage")}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors cursor-pointer"
                style={{ fontFamily: "Urbanist, sans-serif" }}
              >
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
              style={{ fontFamily: "Urbanist, sans-serif" }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin h-5 w-5" />
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <p
            className="mt-6 text-center text-gray-500"
            style={{ fontFamily: "Urbanist, sans-serif" }}
          >
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/SignUpPage")}
              className="text-blue-600 hover:text-blue-700 font-semibold transition-colors cursor-pointer"
            >
              Sign up free
            </button>
          </p>
        </div>

        {/* Active Session Dialog */}
        {showSessionDialog && (
          <ActiveSessionDialog
            onCancel={() => setShowSessionDialog(false)}
            onConfirm={() => {
              setShowSessionDialog(false);
              handelLogin(true);
            }}
          />
        )}

        {/* Footer */}
        <div
          className="flex items-center justify-center gap-6 mt-6 text-sm text-gray-400"
          style={{ fontFamily: "Urbanist, sans-serif" }}
        >
          <a
            href="https://chromewebstore.google.com/detail/nogrunt-qa-automation/neankkcidljlljifcclgcecadeimmolp"
            className="hover:text-blue-600 transition-colors cursor-pointer"
          >
            Get Chrome Extension
          </a>
          <span>•</span>
          <a
            href="https://www.nogrunt.com/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-600 transition-colors cursor-pointer"
          >
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
